import { useEffect, useRef } from 'react'
import { cn } from '@stratascratch/ui'
import useHeadings from '../hooks/useHeadings'

function isVisible(element: Element) {
  const rect = element.getBoundingClientRect()
  return rect.top > 0 && rect.bottom < window.innerHeight
}

interface Props {
  headings: HTMLHeadingElement[]
  activeID: string | undefined
}

function Headings({ headings, activeID }: Props) {
  const ref = useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (ref.current && !isVisible(ref.current)) {
      ref.current.scrollIntoView({ block: 'nearest' })
    }
  }, [activeID])

  return (
    <ul>
      {headings.map((heading) => {
        const current = activeID === heading.id
        const headingLevel = heading.nodeName
        return (
          <li
            key={heading.id}
            className="border-s-2"
            ref={current ? ref : null}
          >
            <a
              href={`#${heading.id}`}
              className={cn(
                current && 'bg-(--bg-color-primary) font-bold',
                current
                  ? 'border-(--color-primary)'
                  : 'border-(--color-border)',
                headingLevel === 'H2' && 'py-2 ps-4',
                headingLevel === 'H3' && 'py-1 ps-8',
                'block text-sm',
              )}
            >
              {heading.innerText}
            </a>
          </li>
        )
      })}
    </ul>
  )
}

function TableOfContents() {
  const { headings, activeID } = useHeadings()
  const ref = useRef<HTMLDivElement>(null)

  if (!headings || headings.length === 0) return null

  return (
    <div
      className="hidden min-[900px]:sticky min-[900px]:top-4 min-[900px]:block min-[900px]:max-h-[calc(100vh-2rem)] min-[900px]:overflow-y-auto"
      ref={ref}
    >
      <nav
        className="bg-(--bg-color-dim) rounded-lg p-6"
        aria-label="Table of contents"
      >
        <h5 className="border-s-2 py-2 ps-4 font-bold">On this page</h5>
        <Headings headings={headings} activeID={activeID} />
      </nav>
    </div>
  )
}

export default TableOfContents
